import axios from 'axios';
import { auth } from '../firebase';
import { getApiUrl, getAuthHeaders } from '../utils/apiConfig';

export interface MapPricingRecord {
  id?: string;
  model: string;
  description?: string;
  brand?: string;
  category?: string;
  productId?: string;
  canonicalProductId?: string;
  needsIngestion?: boolean;
  productStatus?: 'canonical' | 'placeholder' | 'missing' | 'bundle_rebate';
  inferredProductType?: 'camera' | 'lens' | 'accessory';
  bundleOffer?: string;
  bundleCondition?: string;
  mapPrice?: number;
  surePrice?: number;
  sppgPrice?: number;
  effectiveDate?: {
    _seconds: number;
    _nanoseconds: number;
  };
  expirationDate?: {
    _seconds: number;
    _nanoseconds: number;
  };
  source?: string;
  status?: string;
  createdAt?: {
    _seconds: number;
    _nanoseconds: number;
  };
  updatedAt?: {
    _seconds: number;
    _nanoseconds: number;
  };
  // Legacy fields for backward compatibility
  sellingPriceFrom?: number;
  sellingPriceTo?: number;
  sellingAfterIRFrom?: number;
  sellingAfterIRTo?: number;
  startDate?: string;
  endDate?: string;
  changeDate?: string;
  agreementNumber?: string;
  priceType?: string;
  irFrom?: number;
  irTo?: number;
  tcFrom?: number;
  tcTo?: number;
  comments?: string;
}

export interface MapPricingImportResult {
  success: boolean;
  recordsProcessed: number;
  recordsCreated: number;
  recordsUpdated: number;
  errors: string[];
  warnings: string[];
}

export interface MapPricingStats {
  totalRecords: number;
  activeRecords: number;
  changedRecords: number;
  newRecords: number;
  deletedRecords: number;
  averagePriceChange: number;
  priceIncreaseCount: number;
  priceDecreaseCount: number;
  modelsAffected: number;
}

class MapPricingApiService {
  private async getHeaders() {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.log('Auth token not available');
    }
    
    return headers;
  }

  async getMapPricingRecords(params?: {
    model?: string;
    brand?: string;
    category?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: MapPricingRecord[]; count: number; total?: number }> {
    const response = await axios.get(`${getApiUrl()}/map-pricing`, {
      params,
      headers: await this.getHeaders()
    });
    return response.data;
  }

  async getMapPricingRecordById(id: string): Promise<MapPricingRecord> {
    const response = await axios.get(`${getApiUrl()}/map-pricing/${id}`, {
      headers: await this.getHeaders()
    });
    return response.data.data;
  }

  async createMapPricingRecord(record: Omit<MapPricingRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<MapPricingRecord> {
    const response = await axios.post(`${getApiUrl()}/map-pricing`, record, {
      headers: await this.getHeaders()
    });
    return response.data.data;
  }

  async updateMapPricingRecord(id: string, record: Partial<MapPricingRecord>): Promise<MapPricingRecord> {
    const response = await axios.put(`${getApiUrl()}/map-pricing/${id}`, record, {
      headers: await this.getHeaders()
    });
    return response.data.data;
  }

  async deleteMapPricingRecord(id: string): Promise<void> {
    await axios.delete(`${getApiUrl()}/map-pricing/${id}`, {
      headers: await this.getHeaders()
    });
  }

  async importMapPricingData(file: File): Promise<MapPricingImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(`${getApiUrl()}/map-pricing/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(await this.getHeaders())
      }
    });
    return response.data.data;
  }

  async getMapPricingStats(): Promise<MapPricingStats> {
    const response = await axios.get(`${getApiUrl()}/map-pricing/stats`, {
      headers: await this.getHeaders()
    });
    return response.data?.data || response.data;
  }

  async associateWithCameraModels(): Promise<{ associated: number; notFound: string[] }> {
    const response = await axios.post(`${getApiUrl()}/map-pricing/associate`, {}, {
      headers: await this.getHeaders()
    });
    return response.data.data;
  }
}

export const mapPricingApiService = new MapPricingApiService();

/**
 * Rebate programs (pre-release flag `rebate_programs`): a store's scoped
 * rebate offers and the decision on the ones waiting. See
 * `/store/rebate-programs` on the API.
 */
/** What a store attested on a private rebate sheet (flag store_attested_rebates). */
export interface StoreRebateAttestation {
  kind: 'vendor_program' | 'store_funded';
  vendorReference?: string;
  note?: string;
  attestedBy: string;
  attestedByName?: string;
  attestedAt: string | null;
  statement: string;
}

export interface StoreRebateProgram {
  id: string;
  name: string;
  brand: string;
  kind: 'instant_rebate' | 'trade_credit' | 'bundle';
  source: 'vendor_sheet' | 'vendor_private_sheet' | 'store_sheet' | 'manual';
  ownerOrganizationId: string;
  organizationId: string | null;
  scope: { kind: 'all' | 'location' | 'integration'; locationId?: string | null; integrationId?: string | null };
  startAt: string | null;
  endAt: string | null;
  status: 'draft' | 'pending_store_approval' | 'active' | 'declined' | 'expired';
  storeAttestation?: StoreRebateAttestation;
  createdAt: string | null;
}

/** A store-run program on a vendor's brand, from GET /vendor/rebate-programs/store-attested. */
export type VendorStoreAttestedProgram = StoreRebateProgram & { organizationName: string | null };

export async function listVendorStoreAttestedPrograms(): Promise<VendorStoreAttestedProgram[]> {
  const response = await axios.get(`${getApiUrl()}/vendor/rebate-programs/store-attested`, {
    headers: await getAuthHeaders(),
  });
  return response.data?.data || [];
}

export async function listStoreRebatePrograms(status?: StoreRebateProgram['status']): Promise<StoreRebateProgram[]> {
  const response = await axios.get(`${getApiUrl()}/store/rebate-programs`, {
    headers: await getAuthHeaders(),
    params: status ? { status } : undefined,
  });
  return response.data?.data || [];
}

export async function decideStoreRebateProgram(
  id: string,
  action: 'approve' | 'decline',
): Promise<StoreRebateProgram> {
  const response = await axios.post(`${getApiUrl()}/store/rebate-programs/${id}/${action}`, {}, {
    headers: await getAuthHeaders(),
  });
  return response.data?.data;
}
